import {
  ApplicationConfigurationStore,
  CommonStore,
  IApplicationScreenModel,
  IApplicationsScreensSearchFilterModel,
  ICommonAppState,
} from "@xala/common-services";
import {
  Button,
  Dropdown,
  Heading,
  Icon,
  InputSearch,
  ITableColumnProps,
  ITableFilter,
  ITablePaginationConfig,
  ITableRowSelection,
  Menu,
  MenuItem,
  MenuItemClickEvent,
  Modal,
  Table,
} from "@xala/common-ui";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ScreenTypeHelper } from "../../helpers";
import { PlatformTypeHelper } from "../../../../helpers";
import "./ApplicationScreensBrowserModal.scss";

export interface IApplicationScreensBrowserModalProps {
  visible: boolean;
  filter?: IApplicationsScreensSearchFilterModel;
  onCancel?: () => void;
  onSelect?: (row: IApplicationScreenModel) => void;
}

const applicationConfigurationSelector = (state: ICommonAppState) =>
  state.applicationConfiguration;

export const ApplicationScreensBrowserModal: React.FC<IApplicationScreensBrowserModalProps> = (
  props
) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedRow, setSelectedRow] = useState<IApplicationScreenModel>();
  const { screensBrowser, screensTypes } = useSelector(
    applicationConfigurationSelector
  );
  const { platforms } = useSelector((state: ICommonAppState) => state.common);

  const filter: IApplicationsScreensSearchFilterModel = {
    PageNumber: 1,
    PageSize: 10,
    IncludeCount: true,
    IncludePlatforms: true,
    ...screensBrowser.Filter,
    ...props.filter,
  };
  const visible = props.visible;

  /** When this changes, parent passed new criteria (e.g. screen type / app id); refetch without looping on browse success. */
  const propsFilterSignature = JSON.stringify(props.filter ?? {});

  const browseApplicationsScreens = useCallback(
    (newFilter: IApplicationsScreensSearchFilterModel) =>
      dispatch(ApplicationConfigurationStore.Actions.browseScreens(newFilter)),
    [dispatch]
  );

  useEffect(() => {
    if (!visible) {
      return;
    }
    if (screensTypes.length === 0) {
      dispatch(ApplicationConfigurationStore.Actions.selectScreensTypes());
    }
    if (platforms.length === 0) {
      dispatch(CommonStore.Actions.selectPlatforms());
    }
    browseApplicationsScreens(filter);
    // Intentionally omit `filter` from deps: it changes after every browse (redux); we only want open + parent filter changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, propsFilterSignature]);

  const onAdditionalOptionsMenuItemClick = (e: MenuItemClickEvent) => {
    switch (e.key) {
      case "OPTION_CLEAR_FILTERS":
        onClearFiltersClick();
        break;
    }
  };

  const _additionalOptionsMenu: React.ReactElement = (
    <Menu onClick={onAdditionalOptionsMenuItemClick}>
      <MenuItem key="OPTION_CLEAR_FILTERS">Clear filters</MenuItem>
    </Menu>
  );

  const onRefreshClick = () => browseApplicationsScreens(filter);

  const onClearFiltersClick = () => {
    const newFilter: IApplicationsScreensSearchFilterModel = {
      PageSize: screensBrowser.Filter?.PageSize || 10,
      PageNumber: 1,
      IncludeCount: true,
    };
    browseApplicationsScreens(newFilter);
  };

  const getColumnsProps = (): Array<
    ITableColumnProps<IApplicationScreenModel>
  > => {
    return [
      {
        key: "Name",
        width: 250,
        dataIndex: "Name",
        ellipsis: true,
        title: t("Name", "Name"),
      },
      {
        key: "ScreenTypeCode",
        width: 200,
        dataIndex: "ScreenTypeCode",
        title: t("type", "Type"),
        filters: screensTypes.map((screenType) => ({
          text: screenType.Name,
          value: screenType.Code,
        })),
        onFilter: (value: any, row: IApplicationScreenModel) => {
          return row.ScreenTypeCode === value;
        },
        filteredValue: filter.ScreenTypeCode ? [filter.ScreenTypeCode] : null,
        render: (text: any, row: IApplicationScreenModel, index: number) =>
          ScreenTypeHelper.getTag(row.ScreenTypeCode),
      },
      {
        key: "Platforms",
        width: 300,
        dataIndex: "Platforms",
        title: t("Platforms", "Platforms"),
        filters: platforms.map((platform) => ({
          text: platform.DisplayName,
          value: platform.Code,
        })),
        onFilter: (value: any, row: IApplicationScreenModel) => {
          return row.Platforms?.map(
            (platform) => platform.PlatformCode === value
          ).find((el) => el === true)
            ? true
            : false;
        },
        render: (text: any, row: IApplicationScreenModel) => {
          const platformsView: React.ReactNode[] = [];
          console.log(row.Platforms);

          if (row.Platforms && row.Platforms.length > 0) {
            for (const platform of row.Platforms) {
              platformsView.push(
                PlatformTypeHelper.getTag(platform.PlatformCode)
              );
            }
          }

          return platformsView;
        },
      },
    ];
  };

  const onTableChange = (
    pagination: ITablePaginationConfig,
    incomingFilters: ITableFilter
  ) => {
    const newFilter: IApplicationsScreensSearchFilterModel = {
      ...filter,
      PageSize: pagination.pageSize,
      PageNumber: pagination.current,
      ScreenTypeCode: incomingFilters.ScreenTypeCode?.[0]?.toString(),
    };

    browseApplicationsScreens(newFilter);
  };

  const onSearch = (value: string) => {
    const newFilter: IApplicationsScreensSearchFilterModel = {
      ...filter,
      FullTextSearch: value,
      PageNumber: 1,
    };

    browseApplicationsScreens(newFilter);
  };

  const tablePagination: ITablePaginationConfig = {
    pageSize: filter?.PageSize,
    total: screensBrowser.TotalCount,
    current: filter?.PageNumber,
    showTotal: (total, range) =>
      t("ITEMS_RANGE_LABEL", {
        rangeFrom: range[0],
        rangeTo: range[1],
        total: total,
      }),
    showSizeChanger: true,
    defaultPageSize: 10,
    pageSizeOptions: ["10", "30", "50", "100"],
  };
  const columns = getColumnsProps();

  const onCancelClick = () => {
    const { onCancel } = props;

    if (onCancel) {
      onCancel();
    }
  };

  const onSelectClick = () => {
    const { onSelect } = props;

    if (onSelect && selectedRow) {
      onSelect(selectedRow);
    }
  };

  const onTableRow = (row: IApplicationScreenModel, index?: number) => {
    return {
      onClick: (event: React.MouseEvent) => {
        setSelectedRow(row);
      },
      onDoubleClick: (event: React.MouseEvent) => {
        const { onSelect } = props;

        if (onSelect) {
          onSelect(row);
        } else {
          setSelectedRow(row);
        }
      },
    };
  };

  const rowSelection: ITableRowSelection<IApplicationScreenModel> = {
    onChange: (
      selectedRowKeys: React.Key[],
      selectedRows: IApplicationScreenModel[]
    ) => {
      if (selectedRows.length > 0) {
        setSelectedRow(selectedRows[0]);
      }
    },
    type: "radio",
    selectedRowKeys: selectedRow?.Id ? [selectedRow.Id] : [],
  };

  const hasSelectedRow = selectedRow ? true : false;

  console.log(screensBrowser);
  

  return (
    <>
      <Modal
        title={
          <Heading
            title={t("APPLICATION_SCREENS_BROWSER_TITLE")}
            actions={[
              <InputSearch
                key="search"
                placeholder="Search"
                defaultValue={filter?.FullTextSearch}
                onSearch={onSearch}
                style={{ width: 250 }}
                allowClear
              />,
              <Button
                key="reload"
                icon={<Icon type="reload" />}
                onClick={onRefreshClick}
                title="Refresh data"
              />,
              <Dropdown key="more-options" overlay={_additionalOptionsMenu}>
                <Button key="option-more" icon={<Icon type="more" />}></Button>
              </Dropdown>,
            ]}
          />
        }
        onCancel={onCancelClick}
        visible={props.visible}
        className="ApplicationScreensBrowserModal"
        closable={false}
        centered={true}
        width="900px"
        footer={
          <>
            <Button key="cancel" onClick={onCancelClick}>
              Cancel
            </Button>
            <Button
              key="select"
              disabled={!hasSelectedRow}
              onClick={onSelectClick}
            >
              Select
            </Button>
          </>
        }
      >
        <Table<IApplicationScreenModel>
          rowKey="Id"
          style={{ minHeight: "40vh" }}
          columns={columns}
          dataSource={screensBrowser.Entities}
          loading={screensBrowser.IsLoading}
          pagination={tablePagination}
          onChange={onTableChange}
          rowSelection={rowSelection}
          onRow={onTableRow}
        />
      </Modal>
    </>
  );
};
